import React, { useEffect, useRef, useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import axios from 'axios';

import usePublicSettings from '../hooks/usePublicSettings';

const SCRIPT = 'https://accounts.google.com/gsi/client';
let scriptPromise = null;
const loadGoogle = () => {
    if (window.google?.accounts?.id) return Promise.resolve();
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = SCRIPT; s.async = true; s.defer = true;
            s.onload = resolve; s.onerror = () => { scriptPromise = null; reject(new Error('Could not load Google sign-in.')); };
            document.head.appendChild(s);
        });
    }
    return scriptPromise;
};

// Official Google button. Renders nothing unless Google login is on in Admin → Integrations.
// onSuccess receives { currentUser, token, created } from our API.
const GoogleButton = ({ text = 'continue_with', onSuccess, onError }) => {

    const settings = usePublicSettings();
    const clientId = settings?.googleClientId;
    const ref = useRef(null);
    const handlers = useRef({ onSuccess, onError });
    handlers.current = { onSuccess, onError };
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!clientId || !ref.current) return;
        let alive = true;
        const w = Math.min(400, ref.current.offsetWidth || 320);
        setWidth(w);
        loadGoogle()
            .then(() => {
                if (!alive) return;
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    ux_mode: 'popup',
                    callback: async ({ credential }) => {
                        try {
                            const { data } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/users/google`, { credential });
                            handlers.current.onSuccess?.(data);
                        } catch (e) {
                            handlers.current.onError?.(e.response?.data?.error || 'Google sign-in failed.');
                        }
                    }
                });
                window.google.accounts.id.renderButton(ref.current, { theme: 'outline', size: 'large', shape: 'pill', text, width: w, logo_alignment: 'center' });
            })
            .catch(e => handlers.current.onError?.(e.message));
        return () => { alive = false; };
    }, [clientId, text]);

    if (!clientId) return null;

    return (
        <Box mb={5} >
            <Box ref={ref} minHeight='44px' display='flex' justifyContent='center' data-width={width} />
            <Box display='flex' alignItems='center' gap={3} mt={5} >
                <Box flex={1} height='1px' bg='blackAlpha.200' />
                <Text fontSize={13} color='blackAlpha.500' >or use email</Text>
                <Box flex={1} height='1px' bg='blackAlpha.200' />
            </Box>
        </Box>
    );
};

export default GoogleButton;
